import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

export function NotificationCenterCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // key being updated

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications/center');
      setItems(data.items || []);
    } catch { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, enabled) => {
    setBusy(key);
    try {
      await api.put(`/notifications/center/${key}`, { enabled });
      toast.success(enabled ? 'Notification activée' : 'Notification désactivée');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(null);
  };

  const test = async (item) => {
    if (!window.confirm(`Envoyer maintenant un test pour "${item.label}" ?`)) return;
    setBusy(item.key);
    try {
      await api.post(item.test_endpoint);
      toast.success(`Test "${item.label}" déclenché`);
      setTimeout(load, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(null);
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <Card className="border-2 border-[#F97316]/20 shadow-sm bg-gradient-to-br from-[#FFF7ED] to-white" data-testid="notif-center-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#F97316]/15 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-[#F97316]" />
          </div>
          <div>
            <h3 className="text-base font-semibold">Centre de notifications</h3>
            <p className="text-xs text-[#71717A] mt-0.5">
              Activez/désactivez les emails automatiques, vérifiez la prochaine exécution, et envoyez un test à tout moment.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="text-xs text-[#71717A]">Chargement...</p>
        ) : (
          <div className="space-y-2.5">
            {items.map((it) => (
              <div key={it.key}
                className={`p-4 rounded-xl border transition-colors ${it.enabled ? 'bg-white border-[#E5E7EB]' : 'bg-[#FAFAFA] border-[#F4F4F5] opacity-75'}`}
                data-testid={`notif-row-${it.key}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 flex-1 min-w-[260px]">
                    <Switch
                      checked={it.enabled}
                      disabled={busy === it.key}
                      onCheckedChange={(v) => toggle(it.key, v)}
                      data-testid={`notif-toggle-${it.key}`}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#18181B]">{it.label}</p>
                      <p className="text-xs text-[#71717A] mt-0.5">{it.description}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-[#71717A]">
                        <span><strong className="text-[#A1A1AA] uppercase tracking-wider mr-1">Planning</strong>{it.schedule}</span>
                        {it.next_run && (
                          <span><strong className="text-[#A1A1AA] uppercase tracking-wider mr-1">Prochain</strong>{fmt(it.next_run)}</span>
                        )}
                        <span><strong className="text-[#A1A1AA] uppercase tracking-wider mr-1">Destinataires</strong>{it.recipients_count}</span>
                        <span><strong className="text-[#A1A1AA] uppercase tracking-wider mr-1">Dernier envoi</strong>{fmt(it.last_sent)}</span>
                      </div>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => test(it)} disabled={busy === it.key || !it.test_endpoint}
                    className="h-8 text-xs flex-shrink-0" data-testid={`notif-test-${it.key}`}>
                    {busy === it.key ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                    Tester
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
